import React from 'react'
import { Button, FormHelperText, TextField, useTheme } from '@material-ui/core'
import { Container } from '../../components'
import {
    ALL_PRIORITIES,
    PRIORITY_COLORS,
    PRIORITY_LABELS,
    VendorProjectFormData,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { ProjectFieldErrors } from './projectFormHelpers'

interface SectionLabelProps {
    children: React.ReactNode
}

const SectionLabel = ({ children }: SectionLabelProps) => {
    const theme = useTheme()
    return (
        <Container
            style={{
                ...typographyStyle(theme.typography.caption),
                color: theme.palette.text.secondary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: theme.spacing(1),
            }}
        >
            {children}
        </Container>
    )
}

interface Props {
    value: VendorProjectFormData
    onChange: (value: VendorProjectFormData) => void
    errors: ProjectFieldErrors
}

export const ProjectFieldsEditor = ({ value, onChange, errors }: Props) => {
    const theme = useTheme()

    return (
        <>
            <TextField
                label="Title"
                value={value.title}
                onChange={(e) => onChange({ ...value, title: e.target.value })}
                required
                inputProps={{ maxLength: 100 }}
                error={!!errors.title}
                helperText={errors.title}
                variant="outlined"
                size="small"
                fullWidth
            />

            <Container style={{ gap: theme.spacing(2) }}>
                <TextField
                    label="Start Date"
                    type="date"
                    value={value.start_date}
                    onChange={(e) =>
                        onChange({
                            ...value,
                            start_date: e.target.value,
                        })
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                    error={!!errors.start_date}
                    helperText={errors.start_date}
                    variant="outlined"
                    size="small"
                    fullWidth
                />
                <TextField
                    label="End Date"
                    type="date"
                    value={value.end_date}
                    onChange={(e) =>
                        onChange({ ...value, end_date: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                    error={!!errors.end_date}
                    helperText={errors.end_date}
                    variant="outlined"
                    size="small"
                    fullWidth
                />
            </Container>

            <Container direction="column">
                <SectionLabel>Priority</SectionLabel>
                <Container style={{ gap: theme.spacing(1), flexWrap: 'wrap' }}>
                    {ALL_PRIORITIES.map((priority) => {
                        const selected = value.priority === priority
                        const colors = PRIORITY_COLORS[priority]
                        return (
                            <Button
                                key={priority}
                                size="small"
                                variant={selected ? 'contained' : 'outlined'}
                                onClick={() => onChange({ ...value, priority })}
                                style={{
                                    backgroundColor: selected
                                        ? colors.fg
                                        : undefined,
                                    color: selected ? 'white' : colors.fg,
                                    borderColor: colors.fg,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                }}
                            >
                                {PRIORITY_LABELS[priority]}
                            </Button>
                        )
                    })}
                </Container>
                {errors.priority && (
                    <FormHelperText error>{errors.priority}</FormHelperText>
                )}
            </Container>

            <TextField
                label="Address"
                value={value.address ?? ''}
                onChange={(e) =>
                    onChange({ ...value, address: e.target.value })
                }
                variant="outlined"
                size="small"
                fullWidth
            />

            <TextField
                label="Description"
                value={value.description ?? ''}
                onChange={(e) =>
                    onChange({ ...value, description: e.target.value })
                }
                multiline
                rows={3}
                variant="outlined"
                fullWidth
            />
        </>
    )
}
